import type { ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { isChunkLoadError, reloadOnceForChunkFailure } from "@/lib/chunk-reload";

// The last line of defence. App.tsx already self-heals chunk 404s inside the
// React.lazy factory and on vite:preloadError, but a stale chunk can still
// reach here — e.g. a lazy import that rejects on a re-render, or a chunk
// pulled in by something outside lazyWithReload. Without this the customer
// gets a dead screen that only a hard refresh clears, and nobody knows to do
// that. So: reload once for chunk failures, show the error screen for
// everything else, and always offer a Reload button as the manual way out.

function handleError(error: unknown) {
  if (!isChunkLoadError(error)) return;
  reloadOnceForChunkFailure();
}

// Deliberately plain markup rather than <Button>/lucide icons: this renders
// after something has already crashed, so it should depend on as little of
// the app as possible.
function Fallback() {
  return (
    <div className="min-h-[60vh] w-full flex items-center justify-center bg-warm-white px-4 py-16">
      <div className="max-w-md w-full text-center space-y-6">
        <h1 className="font-playfair text-2xl sm:text-3xl text-charcoal">
          Something's Gone Wrong
        </h1>
        <p className="text-charcoal/60 text-base leading-relaxed">
          Sorry, that's on my end. Reloading the page usually sorts it.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-flex items-center justify-center rounded-md bg-soft-blush hover:bg-soft-blush/90 text-white h-11 px-8 text-sm font-medium transition-colors"
          data-testid="error-boundary-reload"
        >
          Reload the page
        </button>
      </div>
    </div>
  );
}

export default function AppErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <Sentry.ErrorBoundary fallback={<Fallback />} onError={handleError}>
      {children}
    </Sentry.ErrorBoundary>
  );
}
